import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Eye } from "lucide-react";
import api from "@/lib/api";

// Feature flags de servicios pendientes: se administran en Admin → IA & Cerebro → «Servicios visibles».
const SiteFlagsContext = createContext({ flags: {}, proximamente: null, loaded: false, preview: [], isOn: () => false, reload: () => {} });

const readPreviewToken = () => {
  try {
    const t = new URLSearchParams(window.location.search).get("preview");
    if (t) sessionStorage.setItem("tf_preview", t);
  } catch { /* ignore */ }
  try { return sessionStorage.getItem("tf_preview") || ""; } catch { return ""; }
};

export function SiteFlagsProvider({ children }) {
  const [state, setState] = useState({ flags: {}, proximamente: null, loaded: false });
  const [preview, setPreview] = useState([]);
  const [previewLoaded, setPreviewLoaded] = useState(!readPreviewToken());
  const reload = useCallback(() => api.get("/api/public/site-flags")
    .then(({ data }) => setState({ ...data, loaded: true }))
    .catch(() => setState((s) => ({ ...s, loaded: true }))), []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const token = readPreviewToken();
    if (!token) return;
    api.get(`/api/public/site-flags/preview?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setPreview(data.flags || []))
      .catch(() => { try { sessionStorage.removeItem("tf_preview"); } catch { /* ignore */ } })
      .finally(() => setPreviewLoaded(true));
  }, []);
  // El público ve solo lo encendido. Un enlace de vista previa habilita servicios extra solo para quien lo abre.
  const isOn = (name) => Boolean(state.flags?.[name]) || preview.includes(name);
  return (
    <SiteFlagsContext.Provider value={{ ...state, preview, previewLoaded, isOn, reload }}>
      {preview.length > 0 && (
        <div className="fixed bottom-3 left-3 z-[60] flex items-center gap-2 rounded-full bg-[#13315C] text-white text-xs font-semibold shadow-lg px-3 py-2" data-testid="preview-banner">
          <Eye size={14} className="text-[#F5B301]" /> Vista previa privada · {preview.length} servicio(s) oculto(s) visibles solo para ti
        </div>
      )}
      {children}
    </SiteFlagsContext.Provider>
  );
}

export const useSiteFlags = () => useContext(SiteFlagsContext);

/** Muestra `children` solo si el flag está encendido (o habilitado por vista previa). */
export const Flag = ({ name, children, fallback = null }) => (useSiteFlags().isOn(name) ? children : fallback);

/** Ruta de servicio pendiente: con el flag apagado (y sin vista previa) redirige al home. */
export const FlagRoute = ({ name, children }) => {
  const { loaded, previewLoaded, isOn } = useSiteFlags();
  if (!loaded || !previewLoaded) return null;
  return isOn(name) ? children : <Navigate to="/" replace />;
};
